import React, { useState, useEffect, useRef } from 'react';
import ChatInterface from './components/ChatInterface';
import BusinessExplorer from './components/BusinessExplorer';
import ContextBreadcrumb from './components/ContextBreadcrumb';
import ModernLanding from './components/ModernLanding';
import { generateConversationId } from './utils/helpers';
import './styles/App.css';

// Load community data from local JSON files
const loadCommunityDataBySlug = async (province, slug) => {
  try {
    // Try to load from static JSON files in public directory
    const response = await fetch(`/data/communities/${province}/${slug}/data.json`);
    if (response.ok) {
      const data = await response.json();
      // Add computed fields
      return {
        ...data,
        theme: slug,
        // Default population/business data if not in JSON
        population: data.population || (slug === 'kemptville' ? 4009 : null),
        businessCount: data.businessCount || (slug === 'kemptville' ? 280 : null)
      };
    }
  } catch (error) {
    console.error('Error loading community data:', error);
  }
  
  // Return basic data if file not found
  return {
    name: slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    slug: slug,
    province: province,
    theme: slug
  };
};

function App() {
  const [community, setCommunity] = useState(null);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [conversationId] = useState(generateConversationId());
  const [contextDepth, setContextDepth] = useState('community'); // community | business | specific
  const [isLoading, setIsLoading] = useState(true);
  const [theme, setTheme] = useState('default');

  // Load community data on mount
  useEffect(() => {
    loadCommunityData();
  }, []);

  const loadCommunityData = async () => {
    // Import domain detector
    const { detectCommunityFromDomain } = await import('./utils/domain-detector.js');
    
    // Detect community from current domain
    const hostname = window.location.hostname;
    const detected = detectCommunityFromDomain(hostname);
    
    if (detected) {
      // Load community data based on detection
      const communityData = await loadCommunityDataBySlug(detected.province, detected.slug);
      setCommunity(communityData);
      setTheme(communityData.theme || detected.slug);
      document.title = `${communityData.name} AI Assistant - Work Local`;
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/community');
      if (!response.ok) {
        // If API fails, default to Kemptville
        console.log('API failed, defaulting to Kemptville');
        setCommunity({
          name: 'Kemptville',
          slug: 'kemptville',
          province: 'ontario',
          theme: 'kemptville'
        });
        setTheme('kemptville');
        document.title = 'Kemptville AI Assistant - Work Local';
        setIsLoading(false);
        return;
      }
      const data = await response.json();
      setCommunity(data);
      setTheme(data.theme || 'default');
      document.title = `${data.name} AI Assistant - Work Local`;
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to load community:', error);
      // Default to Kemptville on error
      setCommunity({
        name: 'Kemptville',
        slug: 'kemptville',
        province: 'ontario',
        theme: 'kemptville'
      });
      setTheme('kemptville');
      document.title = 'Kemptville AI Assistant - Work Local';
      setIsLoading(false);
    }
  };

  const handleBusinessSelect = (business) => {
    setCurrentBusiness(business);
    setContextDepth('business');
  };

  const handleBackToCommunity = () => {
    setCurrentBusiness(null);
    setContextDepth('community');
  };

  const handleDeepDive = (topic) => {
    setContextDepth('specific');
    // Handle specific deep dives (menu items, reviews, etc.)
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading community assistant...</p>
      </div>
    );
  }

  // Use modern landing page for .ai domains
  if (window.location.hostname.endsWith('.ai') && community) {
    return <ModernLanding community={community} />;
  }

  return (
    <div className={`app theme-${theme}`}>
      <header className="app-header">
        <div className="header-content">
          <div className="brand">
            <img 
              src={`/assets/${community?.slug}/logo.png`} 
              alt={`${community?.name} logo`}
              className="community-logo"
              onError={(e) => { e.target.src = '/assets/default-logo.png'; }}
            />
            <div>
              <h1>{community?.name} AI Assistant</h1>
              <p className="tagline">Your local community guide</p>
            </div>
          </div>
          
          <ContextBreadcrumb 
            community={community}
            business={currentBusiness}
            onNavigate={(level) => {
              if (level === 'community') handleBackToCommunity();
            }}
          />
        </div>
      </header>

      <main className="app-main">
        <aside className="sidebar">
          <BusinessExplorer
            community={community}
            currentBusiness={currentBusiness}
            onBusinessSelect={handleBusinessSelect}
          />
        </aside>

        <section className="chat-section">
          <ChatInterface
            conversationId={conversationId}
            community={community}
            currentBusiness={currentBusiness}
            contextDepth={contextDepth}
            onBusinessMention={handleBusinessSelect}
            onDeepDive={handleDeepDive}
          />
        </section>
      </main>

      <footer className="app-footer">
        <p>Powered by Work Local AI • {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}

export default App;